<script lang="ts">
  /**
   * ShareSheet — the header's Share control (spec 2026-09-09 §1b).
   *
   * Shares WHAT IS ON SCREEN (`shareSourceFor`): Swim tab = this swim, Scores tab
   * = this swim's set, Club tab = disabled with a hint. Shows a live preview of
   * the story card (9:16, the Stories path), two toggles (sets, heart rate), one
   * primary "Share", and a tertiary "Export data (JSON)" that is today's
   * firehose export exactly as it was.
   *
   * Card building and rendering come from the share-card module (contract C4,
   * `../lib/shareCard`): the builders over the app's own DerivedWorkout /
   * SetGroup, the seven-rule story line, and the SVG→PNG renderer.
   *
   * Mounted by OverlayHost (stays mounted; `open` toggles), outside the inert
   * #app-shell.
   */
  import { currentTab } from "../lib/nav";
  import { currentDerivedWorkout, currentRawFirehose, selectedWorkoutUUID } from "../lib/state";
  import { workouts } from "../lib/workoutsStore";
  import { getAllSwimFacts } from "../lib/allTimeAggregates";
  import { deriveWithSavedLabels } from "../lib/savedLabels";
  import {
    shareFirehose,
    shareSourceFor,
    applyShareToggles,
    shareStoryCard,
    heroSetGroupFor,
    buildPreview,
    shareKicker,
    shareSetRequest,
    setGroupForShare,
    DEFAULT_SHARE_ITEMS,
    type ShareItems,
  } from "../lib/share";
  import {
    buildShareCardFromSwim,
    buildShareCardFromSet,
    renderShareCard,
    heroSetOf,
    storyForCard,
    type StoryHistory,
  } from "../lib/shareCard";
  import { supportsMethodStore } from "../lib/nativeMethods";
  import type { ShareCard } from "../lib/shareCard/types";
  import { lockBodyScroll, unlockBodyScroll } from "../lib/native/bodyScrollLock";
  import { sheetDrag } from "../lib/sheetDrag";
  import { closeOverlay } from "../lib/overlay";
  import { haptic } from "../lib/haptics";
  import { fmtMeters } from "../lib/format";
  import type { ShareSource } from "../lib/share";
  import SetGroupRibbon from "./SetGroupRibbon.svelte";
  import ShareItemChips from "./ShareItemChips.svelte";
  import { bridge } from "../bridge/client";
  import { setHistoryRows } from "../lib/setHistory";
  import type { SetGroup } from "../lib/setGroups";
  import type { HrPoint } from "../lib/setHrWindow";

  let { open = false }: { open?: boolean } = $props();

  // Lock the page while the sheet is open: the shared lock also stops body, the
  // page's real scroller, which position:fixed alone leaves scrollable (lane
  // share-sheet-dismiss, 2026-09-10). inert on the app shell is owned by
  // App.svelte's reactive attribute. The unlock runs on every close.
  $effect(() => {
    if (!open) return;
    lockBodyScroll();
    return () => unlockBodyScroll();
  });

  // Scores → Your Sets → "Share this set" (requestSetShare) asks for one set by
  // id; that request outranks what the tab would share.
  let requestedSetId = $derived($shareSetRequest);
  let source = $derived<ShareSource>(
    requestedSetId ? { kind: "set", title: "Share this set" } : shareSourceFor($currentTab, $currentDerivedWorkout !== null),
  );

  // The sharer's club name rides on a card as its owner. A set card draws it
  // only as a small "Shared by Scott" byline, so a friend never reads the
  // numbers as their own and the story never names anyone (Scott 12:1x). Asked
  // once per open while the shell can answer, as MenuSheet does; no name, no owner.
  const supportsClubStatus = supportsMethodStore("getClubStatus");
  let ownerName = $state<string | undefined>(undefined);
  $effect(() => {
    if (!open || !$supportsClubStatus) return;
    let cancelled = false;
    bridge
      .getClubStatus()
      .then((s) => {
        if (!cancelled) ownerName = s.myName?.trim() || undefined;
      })
      .catch(() => {
        /* no club status: the card goes out without an owner */
      });
    return () => {
      cancelled = true;
    };
  });

  // The requested set's lengths: from the swim on screen when it swam that set
  // (corrections applied), else from the set's latest swim through the same
  // firehose → deriveWorkout path SetHistorySheet uses.
  // The group and the heart-rate series of the swim it came from, as one value.
  let requestedSet = $state<{ group: SetGroup; hrSeries: readonly HrPoint[] } | null>(null);
  let requestedError = $state<string | null>(null);
  $effect(() => {
    const setId = requestedSetId;
    const dw = $currentDerivedWorkout;
    requestedSet = null;
    requestedError = null;
    if (!open || !setId) return;
    const here = dw ? setGroupForShare(dw, setId) : null;
    if (dw && here) {
      requestedSet = { group: here, hrSeries: dw.hrSeries };
      return;
    }
    const row = setHistoryRows(getAllSwimFacts()).find((r) => r.setId === setId);
    if (!row) {
      requestedError = "This set isn't in your swims yet.";
      return;
    }
    let cancelled = false;
    bridge
      .getWorkoutFirehose({ workoutUUID: row.last.workoutId })
      .then(async (fh) => {
        if (cancelled) return;
        const d = fh ? await deriveWithSavedLabels(fh) : null;
        if (cancelled) return;
        const g = d ? setGroupForShare(d, setId) : null;
        if (d && g) requestedSet = { group: g, hrSeries: d.hrSeries };
        else requestedError = "Couldn't load this set's lengths.";
      })
      .catch(() => {
        if (!cancelled) requestedError = "Couldn't load this set's lengths.";
      });
    return () => {
      cancelled = true;
    };
  });

  // The two switches. Both on by default; reset on close so the next open
  // starts from the full card, never from a stale choice.
  let sets = $state(true);
  let heartRate = $state(true);
  // What goes out (share-chips, 2026-09-12): Image only by default, per share,
  // reset on close with the switches, so every open starts from the default.
  let items = $state<ShareItems>({ ...DEFAULT_SHARE_ITEMS });

  // The swim's ISO start. The firehose carries it; the derived swim only has
  // lap timestamps, which stand in when the raw dump is not around.
  let startDate = $derived(
    $currentRawFirehose?.workout.startDate ??
      ($currentDerivedWorkout?.laps[0] ? new Date($currentDerivedWorkout.laps[0].startMs).toISOString() : ""),
  );

  // The swim's uuid: the firehose carries it; the selection store stands in.
  let uuid = $derived($currentRawFirehose?.workout.uuid ?? $selectedWorkoutUUID ?? "");

  // The full card for the source, then the toggled card the preview and the
  // share both use. History (the swim-facts ledger + the workout list) is read
  // once per open. Shapes are share-card's (C4): ShareSwimSource for the swim
  // card, ONE SetGroup for the set card — the set the swim's own story names
  // (heroSetOf of the swim card), so Swim and Scores share the same hero.
  // Swim tab: "Share this swim" (the whole swim) is the default; "Share a set"
  // opens a picker of this swim's sets ("pick"), and a tapped set shares that
  // set's existing card ("set"). Scott, 2026-09-10 05:56. Scores keeps its set.
  let view = $state<"swim" | "pick" | "set">("swim");
  let pickedIndex = $state<number | null>(null);
  let pickedSet = $derived(pickedIndex !== null ? ($currentDerivedWorkout?.setGroups[pickedIndex] ?? null) : null);
  /** What is being shared right now: a set picked on the Swim tab reads as a set. */
  let shown = $derived<ShareSource>(source.kind === "swim" && view === "set" ? { kind: "set", title: "Share this set" } : source);
  let dialogLabel = $derived(source.kind === "swim" && view === "pick" ? "Share a set" : shown.title);

  let baseCard = $derived.by((): ShareCard | null => {
    if (!open) return null;
    const history: StoryHistory = { facts: getAllSwimFacts(), workouts: $workouts };
    if (requestedSetId) return requestedSet ? buildShareCardFromSet(requestedSet.group, history, ownerName, requestedSet.hrSeries) : null;
    const dw = $currentDerivedWorkout;
    if (!dw) return null;
    if (source.kind === "swim") {
      if (view === "pick") return null;
      if (view === "set") return pickedSet ? buildShareCardFromSet(pickedSet, history, ownerName, dw.hrSeries) : null;
      return buildShareCardFromSwim({ uuid, startDate, derived: dw }, history, undefined, ownerName);
    }
    if (source.kind === "set") {
      const swimCard = buildShareCardFromSwim({ uuid, startDate, derived: dw }, history, undefined, ownerName);
      const set = heroSetGroupFor(dw, heroSetOf(swimCard));
      return set ? buildShareCardFromSet(set, history, ownerName, dw.hrSeries) : null;
    }
    return null;
  });

  function openPicker() {
    haptic("light");
    pickedIndex = null;
    view = "pick";
  }
  function pickSet(i: number) {
    haptic("light");
    pickedIndex = i;
    view = "set";
  }
  function back() {
    haptic("light");
    pickedIndex = null;
    view = view === "set" ? "pick" : "swim";
  }

  // One scroll region serves every view, so a view change starts at its top:
  // "Share a set" is reached by scrolling the swim view down, and the picker
  // must not open mid-list with its Back row out of sight (review, 2026-09-10).
  let region = $state<HTMLDivElement | null>(null);
  $effect(() => {
    void view;
    if (region) region.scrollTop = 0;
  });
  let card = $derived(baseCard ? applyShareToggles(baseCard, { sets, heartRate }) : null);
  // Nothing for the Heart rate switch to switch: a swim with no average, a set with no samples.
  let noHeartRate = $derived(baseCard?.kind === "swim" ? baseCard.avgHr === undefined : baseCard?.set.hr === undefined);

  // Live preview: render the story-size PNG for the current card (buildPreview,
  // share.ts: never throws, hands back a data: URL or a message). A sequence
  // number drops a stale render that resolves after a newer one started.
  // `retryTick` is read by the effect so Retry re-runs it for the same card.
  let previewUrl = $state<string | null>(null);
  let previewReady = $state(false);
  let previewError = $state<string | null>(null);
  let retryTick = $state(0);
  let pngBlob: Blob | null = null;
  let renderSeq = 0;

  function dropPreview() {
    previewUrl = null;
    previewReady = false;
    previewError = null;
    pngBlob = null;
  }

  $effect(() => {
    const c = card;
    void retryTick;
    if (!c) {
      dropPreview();
      return;
    }
    const seq = ++renderSeq;
    previewReady = false;
    previewError = null;
    void buildPreview(c, renderShareCard).then((result) => {
      if (seq !== renderSeq) return;
      if (result.ok) {
        pngBlob = result.blob;
        previewUrl = result.url;
      } else {
        pngBlob = null;
        previewUrl = null;
        previewError = result.message;
      }
    });
  });

  function retryPreview() {
    haptic("light");
    retryTick += 1;
  }

  function onPreviewError() {
    // The data: URL decoded to nothing the browser could draw.
    previewReady = false;
    previewError = "the image could not be decoded";
  }

  function onPreviewLoad() {
    previewReady = true;
  }

  let status = $state<string | null>(null);
  let busy = $state(false);

  // OverlayHost keeps this component mounted and toggles `open`, so opening
  // another overlay (which replaces the single slot) sets open=false WITHOUT
  // calling close(). Reset transient state on close.
  $effect(() => {
    if (!open) {
      status = null;
      sets = true;
      heartRate = true;
      items = { ...DEFAULT_SHARE_ITEMS };
      view = "swim";
      pickedIndex = null;
      shareSetRequest.set(null);
      dropPreview();
    }
  });

  function close() {
    closeOverlay("share");
  }

  function report(result: Awaited<ReturnType<typeof shareStoryCard>>, verb: string) {
    if (result.kind === "shared") {
      status = `${verb}.`;
      haptic("success");
      setTimeout(close, 1200);
    } else if (result.kind === "downloaded") {
      status = "Saved as a file.";
      haptic("success");
    } else if (result.kind === "cancelled") {
      status = null;
    } else {
      status = `Couldn't share: ${result.message}`;
      haptic("error");
    }
  }

  async function onShare() {
    const c = card;
    const png = pngBlob;
    if (!c || !png || busy) return;
    haptic("light");
    busy = true;
    status = null;
    try {
      report(await shareStoryCard(c, png, items), "Shared");
    } catch {
      status = "Couldn't share this card. Tap Share to try again.";
    } finally {
      busy = false;
    }
  }

  async function onExport() {
    const fh = $currentRawFirehose;
    if (!fh || busy) return;
    haptic("light");
    busy = true;
    status = null;
    try {
      report(await shareFirehose(fh), "Sent");
    } catch {
      status = "Couldn't export this swim's data. Tap to try again.";
    } finally {
      busy = false;
    }
  }

  function onKey(e: KeyboardEvent) {
    if (open && e.key === "Escape") close();
  }
</script>

<svelte:window onkeydown={onKey} />

{#if open}
  <div
    class="scrim"
    role="presentation"
    onclick={close}
    onkeydown={(e) => e.key === "Enter" && close()}
  ></div>
  <!-- Drag down from the head, or from the body at its top, to dismiss (the shared gesture). -->
  <div class="sheet" role="dialog" aria-modal="true" aria-label={dialogLabel} data-share-source={source.kind} data-share-view={view} use:sheetDrag={close}>
    <!-- Head: the grabber, and Done at the right. Done is the sheet's own close
         control, as ScoresDrawer and SwimCalendarSheet have: on a phone the
         backdrop above a tall sheet sits under the status bar, and there is no
         Escape key. It stays outside the scroll region, so it is always on
         screen and never under the native tab bar. -->
    <div class="head">
      <div class="grabber" aria-hidden="true"></div>
      <button type="button" class="done" onclick={close}>Done</button>
    </div>
    <!-- The scroll region: it keeps its own pans (pan-y) and never hands them
         on to the page behind (overscroll-behavior: contain). -->
    <div class="body" bind:this={region}>
      {#if source.kind === "swim" && view !== "swim"}
        <button type="button" class="back" onclick={back}>{view === "set" ? "‹ All sets" : "‹ The whole swim"}</button>
      {/if}
      <!-- The kicker names what is being shared AND what it covers; the headline
           IS the card's story line, the same text the card carries. -->
      <p class="kicker">{view === "pick" ? `Share a set · ${$currentDerivedWorkout?.setGroups.length ?? 0} in this swim` : shareKicker(shown, card)}</p>

      {#if source.kind === "disabled" || source.kind === "empty"}
        <p class="hint">{source.hint}</p>
      {:else if requestedSetId && !card}
        <p class="hint" role="status">{requestedError ?? "Loading this set…"}</p>
      {:else if view === "pick" && $currentDerivedWorkout}
        <h2 class="headline">Pick a set to share</h2>
        <!-- Every set of the swim, in swim order, in its stroke colours (the
             app's own ribbon). Tap one to share that set's card. -->
        <ul class="set-list">
          {#each $currentDerivedWorkout.setGroups as g, i (g.index)}
            <li>
              <button
                type="button"
                class="set-row"
                data-set-index={i}
                aria-label={`Share ${g.label}, ${fmtMeters(g.meters)}`}
                onclick={() => pickSet(i)}
              >
                <span class="set-row-text">
                  <span class="set-row-name">{g.label}</span>
                  <span class="set-row-meta">{fmtMeters(g.meters)}</span>
                </span>
                <span class="set-row-chips"><SetGroupRibbon setGroup={g} /></span>
                <span class="chev" aria-hidden="true">›</span>
              </button>
            </li>
          {/each}
        </ul>
      {:else if card}
        <!-- The story as the card draws it: no name; the card's byline says who shared it (Scott 12:1x). -->
        <h2 class="headline">{storyForCard(card).text}</h2>
        {#if storyForCard(card).detail}
          <p class="detail">{storyForCard(card).detail}</p>
        {/if}
        <!-- Live preview: the 9:16 story card, scaled to fit. -->
        <div class="preview-wrap">
          {#if previewError}
            <div class="preview-error" role="alert">
              <p>Couldn't draw the card.</p>
              <p class="why">{previewError}</p>
              <button type="button" class="retry" onclick={retryPreview}>Retry</button>
            </div>
          {:else}
            <img
              class="preview"
              src={previewUrl ?? undefined}
              alt={`Preview of the ${card.kind === "swim" ? "swim" : "set"} card`}
              data-ready={previewReady ? "1" : "0"}
              onload={onPreviewLoad}
              onerror={onPreviewError}
            />
            {#if !previewReady}
              <span class="rendering" aria-live="polite">Drawing your card…</span>
            {/if}
          {/if}
        </div>

        {#if card.kind === "set"}
          <!-- A set card has no set list to hide: the set IS the card. Say so
               rather than leave the space empty (VP round 1, item 3). -->
          <p class="set-note">The set is the card: every length, in its stroke color.</p>
        {/if}
        <!-- Sets switches the swim card only. Heart rate switches both kinds: a
             set card carries its set's heart rate now (share-hr, 2026-09-11), and
             a switch left off on the swim view must be visible on a set's card. -->
        <p class="group-label">On the card</p>
        <div class="toggles">
          {#if card.kind === "swim"}
            <label class="toggle">
              <span class="toggle-text">
                <span class="toggle-title">Sets</span>
                <span class="toggle-sub">The colored sequence, set by set</span>
              </span>
              <input type="checkbox" role="switch" bind:checked={sets} aria-label="Include sets" />
              <span class="switch" aria-hidden="true"></span>
            </label>
          {/if}
          <label class="toggle">
            <span class="toggle-text">
              <span class="toggle-title">Heart rate</span>
              <span class="toggle-sub">
                {#if noHeartRate}No heart rate on this {card.kind}{:else}Your heart rate, if recorded{/if}
              </span>
            </span>
            <input
              type="checkbox"
              role="switch"
              bind:checked={heartRate}
              aria-label="Include heart rate"
              disabled={noHeartRate}
            />
            <span class="switch" aria-hidden="true"></span>
          </label>
        </div>
        <!-- What goes out: the items Share hands iOS, as toggle chips (share-chips, 2026-09-12). -->
        <ShareItemChips bind:items />
      {/if}

      <div class="actions">
        {#if view !== "pick"}
          <button
            type="button"
            class="primary"
            disabled={!card || !previewReady || busy}
            onclick={onShare}
          >
            {busy ? "Sharing…" : "Share"}
          </button>
        {/if}
        {#if source.kind === "swim" && view === "swim" && ($currentDerivedWorkout?.setGroups.length ?? 0) > 0}
          <button type="button" class="secondary" onclick={openPicker}>Share a set</button>
        {/if}
        <button
          type="button"
          class="tertiary"
          disabled={$currentRawFirehose === null || busy}
          onclick={onExport}
        >
          Export data (JSON)
        </button>
      </div>

      {#if status}
        <div class="status" role="status" aria-live="polite">{status}</div>
      {/if}
    </div>
  </div>
{/if}

<style>
  .scrim {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    z-index: 50;
    animation: scrim-in var(--dur-card) var(--ease-swim);
    /* A pan that starts on the backdrop scrolls nothing (every OverlayHost scrim). */
    touch-action: none;
  }
  .sheet {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 51;
    background: var(--bg-card);
    border-top: 1px solid var(--divider);
    border-top-left-radius: 20px;
    border-top-right-radius: 20px;
    padding: 0;
    box-shadow: var(--shadow-floating);
    /* --bg-card is Dawn Glass (42% alpha); the page must not read through a
       sheet's own text, so blur what is behind it (DayPicker's treatment). */
    backdrop-filter: blur(24px) saturate(1.2);
    -webkit-backdrop-filter: blur(24px) saturate(1.2);
    animation: sheet-in var(--dur-card) var(--ease-swim);
    /* The top edge stays below the status bar: the backdrop above a tall sheet
       is still there to tap, and Done is never under the clock. */
    max-height: calc(100vh - 48px - env(safe-area-inset-top, 0px));
    overflow: hidden;
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }
  .head {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    min-height: 44px;
    padding: 0 var(--space-4);
  }
  /* ScoresDrawer's Done. */
  .done {
    margin-left: auto;
    min-height: 44px;
    padding: 0 var(--space-2);
    margin-right: calc(-1 * var(--space-2));
    border: 0;
    background: transparent;
    color: var(--accent-primary);
    font: inherit;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }
  .body {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    touch-action: pan-y; /* NEVER touch-action: none (WKWebView trap) */
    overscroll-behavior: contain;
    /* Room under the last row for the floating native tab bar: the same
       reservation App.svelte's `main` makes, so every row scrolls clear of it. */
    padding: 0 var(--space-4) calc(72px + var(--space-3) + env(safe-area-inset-bottom, 0px));
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }
  /* Top centre of the sheet, where it always sat; Done shares its row. */
  .grabber {
    position: absolute;
    top: 10px;
    left: 50%;
    width: 36px;
    height: 4px;
    margin-left: -18px;
    border-radius: 999px;
    background: var(--divider-strong);
  }
  .kicker {
    margin: 0;
    font: var(--type-caption);
    color: var(--text-muted);
    letter-spacing: 0.08em;
    text-transform: uppercase;
    text-align: center;
  }
  .headline {
    margin: 0;
    font: var(--type-title-ribbon);
    letter-spacing: -0.01em;
    color: var(--text-primary);
    text-align: center;
    text-wrap: balance;
  }
  .detail {
    margin: calc(-1 * var(--space-2)) 0 0;
    font: var(--type-body-sm);
    color: var(--accent-tertiary);
    text-align: center;
  }
  .hint {
    margin: 0;
    padding: var(--space-3);
    font: var(--type-body-sm);
    color: var(--text-muted);
    text-align: center;
    background: var(--bg-surface);
    border: 1px dashed var(--divider);
    border-radius: var(--radius-card);
  }

  .preview-wrap {
    position: relative;
    align-self: center;
    width: 162px;
    aspect-ratio: 9 / 16;
    /* Full size on every sheet, as on the set sheet: overflow:hidden drops a
       flex item's automatic minimum to 0, so a short sheet squeezed the box and
       cropped the card. The region scrolls instead. */
    min-height: 288px;
    border-radius: var(--radius-card);
    overflow: hidden;
    background: var(--bg-surface);
    border: 1px solid var(--divider);
  }
  .preview {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  .preview[data-ready="0"] {
    opacity: 0;
  }
  .rendering {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font: var(--type-caption);
    color: var(--text-muted);
  }
  .preview-error {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: var(--space-2);
    padding: var(--space-3);
    text-align: center;
  }
  .preview-error p {
    margin: 0;
    font: var(--type-body-sm);
    color: var(--text-primary);
  }
  .preview-error .why {
    font: var(--type-caption);
    color: var(--text-muted);
  }
  .retry {
    padding: var(--space-2) var(--space-3);
    border-radius: var(--radius-button);
    border: 1px solid var(--accent-primary);
    background: transparent;
    color: var(--accent-primary);
    font: var(--type-caption);
    font-weight: 600;
    letter-spacing: 0.04em;
    cursor: pointer;
  }

  .set-note {
    margin: 0;
    padding: var(--space-3);
    font: var(--type-body-sm);
    color: var(--text-muted);
    text-align: center;
    background: var(--bg-surface);
    border: 1px solid var(--divider);
    border-radius: var(--radius-card);
  }
  /* "On the card", the switches' heading: the chips' "What goes out" sits under them. */
  .group-label {
    margin: 0 0 calc(-1 * var(--space-2));
    font: var(--type-caption);
    color: var(--text-muted);
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }
  .toggles {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }
  .toggle {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-3);
    background: var(--bg-surface);
    border: 1px solid var(--divider);
    border-radius: var(--radius-card);
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }
  .toggle-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex: 1 1 auto;
    min-width: 0;
  }
  .toggle-title {
    font: var(--type-title-subtitle);
    color: var(--text-primary);
  }
  .toggle-sub {
    font: var(--type-body-sm);
    color: var(--text-muted);
  }
  /* The real control is the (visually hidden) checkbox; the .switch is its face. */
  .toggle input {
    position: absolute;
    opacity: 0;
    width: 1px;
    height: 1px;
    pointer-events: none;
  }
  .switch {
    flex: 0 0 auto;
    width: 46px;
    height: 28px;
    border-radius: 999px;
    background: var(--bg-raised);
    border: 1px solid var(--divider-strong);
    position: relative;
    transition: background-color var(--dur-micro) var(--ease-swim);
  }
  .switch::after {
    content: "";
    position: absolute;
    top: 3px;
    left: 3px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: var(--text-muted);
    transition: transform var(--dur-micro) var(--ease-swim),
                background-color var(--dur-micro) var(--ease-swim);
  }
  .toggle input:checked + .switch {
    background: var(--accent-primary);
    border-color: var(--accent-primary);
  }
  .toggle input:checked + .switch::after {
    transform: translateX(18px);
    background: var(--bg-canvas);
  }
  .toggle input:focus-visible + .switch {
    outline: 2px solid var(--accent-yellow);
    outline-offset: 2px;
  }
  .toggle:has(input:disabled) {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .actions {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }
  .primary {
    width: 100%;
    padding: var(--space-3);
    border-radius: var(--radius-card);
    border: 1px solid var(--accent-primary);
    background: var(--accent-primary);
    color: var(--bg-canvas);
    font: var(--type-title-subtitle);
    font-weight: 600;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    transition: opacity var(--dur-micro) var(--ease-swim);
  }
  .primary:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
  .primary:focus-visible {
    outline: 2px solid var(--accent-yellow);
    outline-offset: 2px;
  }
  .secondary {
    width: 100%;
    padding: var(--space-3);
    border-radius: var(--radius-card);
    border: 1px solid var(--accent-primary);
    background: transparent;
    color: var(--accent-primary);
    font: var(--type-title-subtitle);
    font-weight: 600;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }
  .secondary:focus-visible,
  .done:focus-visible,
  .back:focus-visible,
  .set-row:focus-visible {
    outline: 2px solid var(--accent-yellow);
    outline-offset: 2px;
  }
  .back {
    align-self: flex-start;
    padding: var(--space-2) var(--space-3);
    border-radius: var(--radius-button);
    border: 1px solid var(--divider);
    background: var(--bg-surface);
    color: var(--text-primary);
    font: var(--type-body-sm);
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }
  .set-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }
  .set-row {
    width: 100%;
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: center;
    gap: var(--space-2) var(--space-3);
    padding: var(--space-3);
    background: var(--bg-surface);
    border: 1px solid var(--divider);
    border-radius: var(--radius-card);
    color: inherit;
    text-align: left;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }
  .set-row-text {
    display: flex;
    align-items: baseline;
    gap: var(--space-2);
    min-width: 0;
  }
  .set-row-name {
    font: var(--type-title-subtitle);
    color: var(--text-primary);
  }
  .set-row-meta {
    font: var(--type-body-sm);
    color: var(--text-muted);
    white-space: nowrap;
  }
  .set-row-chips {
    grid-column: 1;
    min-width: 0;
    overflow: hidden;
  }
  /* A lone length in the picker is a set in its own right: full colour, like
     every chip beside it (VP polish item 6: SetGroupRibbon dims it to 60 %). */
  .set-row-chips :global(.chip-lone) {
    opacity: 1;
  }
  .chev {
    grid-column: 2;
    grid-row: 1 / span 2;
    font-size: 24px;
    color: var(--text-muted);
  }
  .tertiary {
    width: 100%;
    padding: var(--space-2);
    border: 0;
    background: transparent;
    color: var(--text-muted);
    font: var(--type-body-sm);
    text-decoration: underline;
    text-underline-offset: 3px;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }
  .tertiary:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
  .tertiary:focus-visible {
    outline: 2px solid var(--accent-primary);
    outline-offset: 2px;
    border-radius: var(--radius-button);
  }
  .status {
    padding: var(--space-2) var(--space-3);
    font: var(--type-caption);
    color: var(--accent-primary);
    text-align: center;
    background: var(--bg-surface);
    border-radius: var(--radius-button);
    letter-spacing: 0.04em;
  }

  @keyframes scrim-in {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
  @keyframes sheet-in {
    from { transform: translateY(100%); }
    to   { transform: translateY(0); }
  }
</style>
